import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  forwardRef,
  HostBinding,
  input,
} from '@angular/core';
import { FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { ShFormControl } from '../form-control/form-control.base';
import { ChipsModule } from 'primeng/chips';

/**
 * A free-text list: type a value and press Enter (or a comma, or leave the
 * box) to add it as a chip. It replaced the Syncfusion `sh-multi-select` with
 * `mustMatchOptions="false"` (a multi select without options) in super-user
 * and keeps its value shapes: `text` is a string array, `number` a number
 * array (values that are not numbers are not added). A value the user did not
 * touch stays as it was stored; emptying the list gives `[]`.
 *
 *   <sh-tag-input formControlName="requiredFields" />
 *   <sh-tag-input type="number" formControlName="includeFileIds" />
 */
@Component({
  selector: 'sh-tag-input',
  changeDetection: ChangeDetectionStrategy.Default,
  imports: [CommonModule, FormsModule, TranslateModule, ChipsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ShTagInputComponent),
      multi: true,
    },
  ],
  template: `
    <p-chips
      [ngModel]="items()"
      (ngModelChange)="onItems($event)"
      [ngModelOptions]="{ standalone: true }"
      [inputId]="controlId()"
      [placeholder]="placeholder() | translate"
      separator=","
      [addOnBlur]="true"
      [allowDuplicate]="false"
      [disabled]="isDisabled() || isReadonly()"
      [style]="fullWidth"
      (onBlur)="markTouched()"
    ></p-chips>
  `,
})
export class ShTagInputComponent extends ShFormControl<(string | number)[]> {
  readonly type = input<'text' | 'number'>('text');

  @HostBinding('class.sh-control') readonly shControl = true;
  @HostBinding('class.sh-invalid') get invalidLook(): boolean {
    return this.isInvalid();
  }

  protected readonly fullWidth = { width: '100%' };

  // A computed, not a getter: the chips must get the same array until the
  // value really changes.
  protected readonly items = computed(() =>
    (this.value() ?? []).map((item) => String(item))
  );

  protected override equals(
    a: (string | number)[] | null,
    b: (string | number)[] | null
  ): boolean {
    if (a === b) return true;
    if (!a || !b) return false;
    return a.length === b.length && a.every((v, i) => v === b[i]);
  }

  protected onItems(items: string[] | null): void {
    const texts = items ?? [];
    if (this.type() === 'number') {
      this.commit(
        texts
          .map((text) => text.trim())
          .filter((text) => text !== '' && Number.isFinite(Number(text)))
          .map(Number)
      );
      return;
    }
    this.commit([...texts]);
  }
}
